import { KeyboardArrowDown } from "@mui/icons-material";
import MenuIcon from "@mui/icons-material/Menu";
import PowerSettingsNewOutlinedIcon from "@mui/icons-material/PowerSettingsNewOutlined";
import SpeedOutlinedIcon from "@mui/icons-material/SpeedOutlined";
import GroupOutlinedIcon from "@mui/icons-material/GroupOutlined";
import LockPersonOutlinedIcon from "@mui/icons-material/LockPersonOutlined";
import UpdateOutlinedIcon from "@mui/icons-material/UpdateOutlined";
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import Menu from "@mui/material/Menu";
import { useAuth } from "contexts/AuthContext";
import { useSidebar } from "contexts/SidebarContext";
import { useEffect, useState } from "react";
import { MenuItem, ProSidebar, Menu as SidebarMenu } from "react-pro-sidebar";
import "react-pro-sidebar/dist/css/styles.css";
import { Link, useNavigate } from "react-router-dom";
import { tokens } from "theme";
import { DashboardIcon } from "./Icons";
import Tooltip from "@mui/material/Tooltip";
import "./sidebar.css";

const Item = ({ title, to, icon, selected, setSelected }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  return (
    <MenuItem
      active={selected === title}
      style={{
        color: colors.grey[100],
      }}
      onClick={() => setSelected(title)}
      icon={icon}
    >
      <Typography>{title}</Typography>
      <Link to={to} />
    </MenuItem>
  );
};

const Sidebar = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const { logout } = useAuth();

  const [isCollapsed, setIsCollapsed] = useState(false);

  const [popupMenu, setPopupMenu] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);

  const [selected, setSelected] = useState("Dashboard");
  const { collapsed, setCollapsed, toggled, closeMobileSidebar } = useSidebar();
  const [openMenus, setOpenMenus] = useState({
    destinations: false,
    pricing: false,
    pnr: false,
    hotels: false,
    settings: false,
  });

  const toggleMenu = (key) => {
    setOpenMenus((prev) =>
      Object.fromEntries(
        Object.keys(prev).map((menuKey) => [
          menuKey,
          menuKey === key ? !prev[key] : false,
        ]),
      ),
    );
  };

  useEffect(() => {
    setIsCollapsed(collapsed);
  }, [collapsed]);

  const handleMenuClick = (menuKey, event) => {
    if (collapsed) {
      setPopupMenu(menuKey);
      setAnchorEl(event.currentTarget);
    } else {
      toggleMenu(menuKey);
    }
  };

  const navigate = useNavigate();

  const handlePopupNavigate = (path) => {
    navigate(path);
    setPopupMenu(null);
    setAnchorEl(null);
  };

  const PopupMenuItem = ({ title, to, icon }) => {
    return (
      <MenuItem
        active={selected === title}
        onClick={() => {
          setSelected(title);
          handlePopupNavigate(to);
        }}
      >
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={{
            color: colors.grey[100],
            "&:hover": {
              color: colors.primary[400],
            },
          }}
        >
          {icon}
          <Typography>{title}</Typography>
        </Box>
      </MenuItem>
    );
  };

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  return (
    <Box
      sx={{
        "& .pro-sidebar-inner": {
          background: `${colors.white} !important`,
        },
        "& .pro-icon-wrapper": {
          backgroundColor: "transparent !important",
        },
        "& .pro-inner-item": {
          padding: "5px 35px 5px 20px !important",
        },
        "& .sb-subitems-wrapper .pro-inner-item": {
          padding: "10px 15px !important",
        },
      }}
    >
      <ProSidebar
        collapsed={collapsed}
        toggled={toggled}
        breakPoint="md"
        onToggle={(value) => {
          if (!value) {
            closeMobileSidebar();
          }
        }}
      >
        <SidebarMenu>
          <Box
            display="flex"
            className={`sb-item-wrapper ${isCollapsed ? "sb-item-center" : "sb-item-start"}`}
          >
            <IconButton onClick={() => setCollapsed((prev) => !prev)}>
              <MenuIcon />
            </IconButton>
            {!isCollapsed && (
              <Box
                sx={{
                  height: "60px",
                  width: "auto",
                  paddingTop: "5px",
                  paddingBottom: "15px",
                }}
                component="img"
                src="../../assets/logo.png"
                alt="Logo"
              ></Box>
            )}
          </Box>

          <div className="sb-item-outer">
            <div
              className={`sb-item-wrapper ${
                isCollapsed ? "sb-item-center" : "sb-item-start"
              }`}
            >
              <Tooltip
                title="Dashboard"
                placement="right"
                disableHoverListener={!isCollapsed}
              >
                <div className="sb-icon-wrapper">
                  <SpeedOutlinedIcon className="dashboard-icon sb-icon" />
                </div>
              </Tooltip>

              {!isCollapsed && (
                <Typography
                  variant="h5"
                  color={colors.grey[300]}
                  selected={selected}
                  setSelected={setSelected}
                >
                  Dashboard
                </Typography>
              )}
            </div>
          </div>

          <div className="sb-item-outer">
            <Box
              component="div"
              className={`sb-item-wrapper ${
                isCollapsed ? "sb-item-center" : "sb-item-start"
              }`}
              style={{ cursor: "pointer" }}
              onClick={(e) => {
                e.stopPropagation();
                handleMenuClick("destinations", e);
              }}
            >
              <Tooltip
                title="Users & Roles"
                placement="right"
                disableHoverListener={!isCollapsed}
              >
                <div className="sb-icon-wrapper">
                  <GroupOutlinedIcon className="destinations-icon sb-icon" />
                </div>
              </Tooltip>

              {!isCollapsed && (
                <div className="sb-arrow-wrapper">
                  <Typography variant="h5" color={colors.grey[300]}>
                    Users & Roles
                  </Typography>

                  <KeyboardArrowDown
                    className={`sb-arrow ${
                      openMenus.destinations ? "sb-arrow-open" : ""
                    }`}
                  />
                </div>
              )}
            </Box>

            {!isCollapsed && (
              <div
                className={`sb-subitems-wrapper ${
                  openMenus.destinations ? "sb-submenu-open" : ""
                }`}
              >
                <div className="sb-item-wrapper">
                  <Item
                    title="Users"
                    to="/users"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
                <div className="sb-item-wrapper">
                  <Item
                    title="Role"
                    to="/roles"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
                <div className="sb-item-wrapper">
                  <Item
                    title="User Role Assignment"
                    to="/user-role-assignment"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sb-item-outer">
            <div
              className={`sb-item-wrapper ${
                isCollapsed ? "sb-item-center" : "sb-item-start"
              }`}
              style={{ cursor: "pointer" }}
              onClick={(e) => handleMenuClick("pricing", e)}
            >
              <Tooltip
                title="Grant Permission"
                placement="right"
                disableHoverListener={!isCollapsed}
              >
                <div className="sb-icon-wrapper">
                  <LockPersonOutlinedIcon className="pricing-icon sb-icon" />
                </div>
              </Tooltip>
              {!isCollapsed && (
                <div className="sb-arrow-wrapper">
                  <Typography variant="h5" color={colors.grey[300]}>
                    Grant Permission
                  </Typography>

                  <KeyboardArrowDown
                    className={`sb-arrow ${
                      openMenus.pricing ? "sb-arrow-open" : ""
                    }`}
                  />
                </div>
              )}
            </div>
            {!isCollapsed && (
              <div
                className={`sb-subitems-wrapper ${
                  openMenus.pricing ? "sb-submenu-open" : ""
                }`}
              >
                <div className="sb-item-wrapper">
                  <Item
                    title="Project Permission"
                    to="/project-permissions"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>

                <div className="sb-item-wrapper">
                  <Item
                    title="Module Permission"
                    to="/module-permissions"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sb-item-outer">
            <div
              className={`sb-item-wrapper ${
                isCollapsed ? "sb-item-center" : "sb-item-start"
              }`}
              style={{ cursor: "pointer" }}
              onClick={(e) => handleMenuClick("pnr", e)}
            >
              <Tooltip
                title="Feature Setup"
                placement="right"
                disableHoverListener={!isCollapsed}
              >
                <div className="sb-icon-wrapper">
                  <UpdateOutlinedIcon className="pnr-icon sb-icon" />
                </div>
              </Tooltip>

              {!isCollapsed && (
                <div className="sb-arrow-wrapper">
                  <Typography variant="h5" color={colors.grey[300]}>
                    Feature Setup
                  </Typography>

                  <KeyboardArrowDown
                    className={`sb-arrow ${
                      openMenus.pnr ? "sb-arrow-open" : ""
                    }`}
                  />
                </div>
              )}
            </div>
            {!isCollapsed && (
              <div
                className={`sb-subitems-wrapper ${
                  openMenus.pnr ? "sb-submenu-open" : ""
                }`}
              >
                <div className="sb-item-wrapper">
                  <Item
                    title="Project"
                    to="/projects"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
                <div className="sb-item-wrapper">
                  <Item
                    title="Project Module"
                    to="/project-modules"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
                <div className="sb-item-wrapper">
                  <Item
                    title="Action"
                    to="/actions"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
                <div className="sb-item-wrapper">
                  <Item
                    title="Module Categories"
                    to="/module-categories"
                    selected={selected}
                    setSelected={setSelected}
                  />
                </div>
              </div>
            )}
          </div>

          <div className="sb-item-outer">
            <div
              className={`sb-item-wrapper ${
                isCollapsed ? "sb-item-center" : "sb-item-start"
              }`}
            >
              <Tooltip
                title="Logout"
                placement="right"
                disableHoverListener={!isCollapsed}
              >
                <div className="sb-icon-wrapper">
                  <PowerSettingsNewOutlinedIcon className="pnr-icon sb-icon" />
                </div>
              </Tooltip>

              {!isCollapsed && (
                <div className="sb-arrow-wrapper">
                  <Typography
                    variant="h5"
                    color={colors.grey[300]}
                    onClick={handleLogout}
                  >
                    Logout
                  </Typography>
                </div>
              )}
            </div>
          </div>
        </SidebarMenu>
      </ProSidebar>

      <Menu
        anchorEl={anchorEl}
        open={Boolean(popupMenu)}
        onClose={() => {
          setPopupMenu(null);
          setAnchorEl(null);
        }}
        anchorOrigin={{ vertical: "center", horizontal: "right" }}
        transformOrigin={{ vertical: "center", horizontal: "left" }}
        disablePortal={false}
        className="sidebar-popup-menu"
      >
        {popupMenu === "destinations" && (
          <Box display="flex" flexDirection="column" gap={1} padding={1}>
            <PopupMenuItem title="Users" to="/users" />
            <PopupMenuItem title="Role" to="/roles" />
            <PopupMenuItem
              title="User Role Assignment"
              to="/user-role-assignment"
            />
          </Box>
        )}

        {popupMenu === "pricing" && (
          <Box display="flex" flexDirection="column" gap={1} padding={1}>
            <PopupMenuItem
              title="Project Permission"
              to="/project-permissions"
            />
            <PopupMenuItem title="Module Permission" to="/module-permissions" />
          </Box>
        )}

        {popupMenu === "pnr" && (
          <Box display="flex" flexDirection="column" gap={1} padding={1}>
            <PopupMenuItem title="Project" to="/projects" />
            <PopupMenuItem title="Project Module" to="/project-modules" />
            <PopupMenuItem title="Action" to="/actions" />
            <PopupMenuItem title="Module Categories" to="/module-categories" />
          </Box>
        )}

        {popupMenu === "hotels" && (
          <Box display="flex" flexDirection="column" gap={1} padding={1}>
            <MenuItem onClick={() => handleLogout()}>
              <Box
                display="flex"
                alignItems="center"
                gap={1}
                sx={{
                  color: colors.grey[100],
                  "&:hover": {
                    color: colors.primary[400],
                  },
                }}
              >
                <Typography>Logout</Typography>
              </Box>
            </MenuItem>
          </Box>
        )}
      </Menu>
    </Box>
  );
};

export default Sidebar;
